import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import { extractApiError, extractHttpStatus } from '@/lib/api-client';
import { cn } from '@/lib/utils';

interface ErrorBannerProps {
  error: unknown;
  className?: string;
}

/**
 * Translates a thrown error into a localized message. Prefers the
 * backend `errors.<CODE>` key; otherwise maps the HTTP status so a
 * permission error (403) reads "no permission" rather than the
 * misleading "could not reach the server" — only a genuine no-response
 * failure falls through to NETWORK. Never renders the raw API
 * `message` (developer-facing).
 */
export function ErrorBanner({ error, className }: ErrorBannerProps): JSX.Element | null {
  const { t } = useTranslation();
  if (!error) return null;

  const apiError = extractApiError(error);
  const code = apiError?.code;
  const status = extractHttpStatus(error);
  let message: string;
  if (code) {
    message = t(`errors.${code}`, { defaultValue: t('errors.GENERIC') });
  } else if (status === 403) {
    message = t('errors.FORBIDDEN');
  } else if (status === 401) {
    message = t('errors.AUTH_SESSION_EXPIRED', { defaultValue: t('errors.GENERIC') });
  } else if (status === 404) {
    message = t('errors.NOT_FOUND');
  } else if (status && status >= 500) {
    message = t('errors.GENERIC');
  } else if (status && status >= 400) {
    message = t('errors.GENERIC');
  } else {
    // No HTTP status at all → genuine network failure / timeout.
    message = t('errors.NETWORK');
  }

  return (
    <div
      role="alert"
      className={cn(
        'flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive',
        className,
      )}
    >
      <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
      <span>{message}</span>
    </div>
  );
}
